import { normalizeShadowcamProfileName, profileForShadowcamName, trackMatchesVideoConstraints,
  videoConstraintsForShadowcam } from '../../shadowcamCaptureProfiles';
import type { DriverState } from '@playcastdotio/CoreTypes';
import { resolveShadowcamDevice } from '../ShadowcamCaptureResolver';
import type { DeviceConfigManager } from '../DeviceConfigManager';
import type { TrackCaptureManager, PreparedVideoCapture } from '../TrackCaptureManager';
import type { QualityTierDemotionContext } from '../../../peer/managers/RecoveryCapacityGate';

type RequestPreparation = { attempts: number; prepared?: PreparedVideoCapture; cleanup: ReturnType<typeof setTimeout> };

/** Chooses capture constraints while keeping publication separate from acquisition. */
export class ShadowcamCaptureManager {
  private readonly requests = new Map<string, RequestPreparation>();
  private readonly selections = new WeakMap<PreparedVideoCapture, { name: string; deviceId: string; requestId?: string }>();
  private nextRequest = 0;
  constructor(private readonly deps: {
    devices: Pick<DeviceConfigManager, 'enumerateDevices' | 'getShadowcams' | 'getCameras' | 'selectCamera'> & {
      frameRateForDevice?: (deviceId: string) => number | undefined;
    };
    tracks: Pick<TrackCaptureManager, 'getVideoTrack' | 'captureVideo' | 'prepareVideo' | 'commitPreparedVideo' | 'discardPreparedVideo'>;
    committed: (name: string) => void;
    getDriverState?: () => DriverState | Record<string, boolean> | undefined;
    nowMs?: () => number;
  }) {}

  /**
   * Reuse a matching live track or capture and publish the selected ShadowCam (the
   * first when unnamed) or OBS camera. Returns null if no device or track is available.
   * Superseded requests, verified capture failures, and selection/notification errors
   * propagate; successful capture persists the device selection.
   */
  async start(shadowcamName?: string, context?: { isCurrent: () => boolean }): Promise<MediaStreamTrack | null> {
    if (context?.isCurrent() === false) throw new Error('Capture superseded');
    const existing = this.deps.tracks.getVideoTrack();
    const id = existing?.getSettings().deviceId;
    if (existing?.readyState === 'live' && id && shadowcamName && existing.label.includes(shadowcamName)
      && (trackMatchesVideoConstraints(existing, this.constraints(shadowcamName, id, 'exact'))
        || trackMatchesVideoConstraints(existing, this.constraints(shadowcamName, id, 'fallback')))) return existing;
    const selection = await this.select(shadowcamName);
    if (!selection) return null;
    const { name, deviceId } = selection;
    const track = await this.deps.tracks.captureVideo(this.constraints(name, deviceId, 'exact'), {
      verifyProfile: this.verifyProfile(name, deviceId, 'exact'), shadowcamName: name, isCurrent: context?.isCurrent,
    });
    if (track) { this.deps.devices.selectCamera(deviceId); this.deps.committed(name); }
    return track;
  }

  /**
   * Acquire without publishing. Calls sharing a requestId reuse a prepared capture
   * or try the advertised FPS first, then the exact size preferring 60 FPS (any
   * rate the device supports there verifies), with at most two acquisitions.
   * hostDeadlineMs is an absolute millisecond timestamp; absent deadlines use five
   * seconds from this call. Preparation errors propagate, including cancellation,
   * deadline/budget exhaustion and missing-device errors (retryable).
   */
  async prepare(shadowcamName: string, context: QualityTierDemotionContext): Promise<PreparedVideoCapture | null> {
    const requestId = context.requestId ?? `capture-${++this.nextRequest}`;
    const deadline = context.hostDeadlineMs ?? this.now() + 5000;
    this.assertCurrent(context, deadline);
    let request = this.requests.get(requestId);
    if (!request) {
      const cleanup = setTimeout(() => this.clearRequest(requestId), Math.max(0, deadline + 1000 - this.now()));
      request = { attempts: 0, cleanup }; this.requests.set(requestId, request);
    }
    if (request.prepared) return request.prepared;
    if (request.attempts >= 2) throw new Error('Capture acquisition budget exhausted');
    const selection = await ShadowcamCaptureManager.beforeDeadline(this.select(shadowcamName), context, deadline, () => this.now());
    if (!selection) throw Object.assign(new Error('Resolution capture unavailable'), { code: 'capture-preparation-failed', retryable: true });
    this.assertCurrent(context, deadline);
    const { name, deviceId } = selection;
    const mode = request.attempts++ === 0 ? 'exact' : 'fallback';
    const prepared = await this.deps.tracks.prepareVideo(this.constraints(name, deviceId, mode), {
      verifyProfile: this.verifyProfile(name, deviceId, mode), shadowcamName: name,
      isCurrent: context.isCurrent, hostDeadlineMs: deadline, maxAcquisitionAttempts: 1,
    });
    if (prepared) {
      request.prepared = prepared;
      this.selections.set(prepared, { name, deviceId, requestId });
    }
    return prepared;
  }

  commit(name: string, prepared: PreparedVideoCapture, context: { isCurrent: () => boolean }): MediaStreamTrack {
    const selection = this.selections.get(prepared);
    if (!selection || selection.name !== name || !context.isCurrent()) throw new Error('Capture superseded');
    // MediaCoordinator publishes the camera name before this synchronous commit.
    const track = this.deps.tracks.commitPreparedVideo(prepared, context.isCurrent);
    this.deps.committed(selection.name);
    // Persistence cannot invalidate capture after it has been published to observers.
    try { this.deps.devices.selectCamera(selection.deviceId); }
    catch (error) { console.warn('Could not persist selected capture device', error); }
    if (selection.requestId) this.clearRequest(selection.requestId);
    return track;
  }

  discard(prepared: PreparedVideoCapture): void {
    const requestId = this.selections.get(prepared)?.requestId;
    this.deps.tracks.discardPreparedVideo(prepared);
    if (requestId) this.clearRequest(requestId);
    this.selections.delete(prepared);
  }

  dispose(): void {
    for (const key of this.requests.keys()) this.clearRequest(key);
  }

  static beforeDeadline<T>(operation: Promise<T>, context: { isCurrent: () => boolean }, deadline: number,
    nowMs: () => number = () => Date.now()): Promise<T> {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error('Capture deadline exceeded')), Math.max(0, deadline - nowMs()));
      void operation.then(value => {
        clearTimeout(timer);
        if (!context.isCurrent()) reject(new Error('Capture superseded'));
        else if (nowMs() >= deadline) reject(new Error('Capture deadline exceeded'));
        else resolve(value);
      }, error => { clearTimeout(timer); reject(error); });
    });
  }

  private constraints(name: string, deviceId: string, mode: 'exact' | 'fallback') {
    return videoConstraintsForShadowcam(deviceId, name, mode, mode === 'exact' ? this.requestedFrameRate(deviceId) : undefined);
  }

  private verifyProfile(name: string, deviceId: string, mode: 'exact' | 'fallback') {
    const profile = profileForShadowcamName(name);
    if (!profile) return undefined;
    // Fallback verifies only the exact size: the device chose the rate it supports there.
    const requested = mode === 'fallback' ? 0 : this.requestedFrameRate(deviceId);
    return { ...profile, frameRate: requested ?? 0 };
  }

  private requestedFrameRate(deviceId: string): number | undefined {
    const fps = this.deps.devices.frameRateForDevice?.(deviceId);
    return fps && fps > 0 ? fps : undefined;
  }

  private async select(shadowcamName?: string): Promise<{ name: string; deviceId: string } | undefined> {
    await this.deps.devices.enumerateDevices();
    if (shadowcamName === 'OBS') {
      const obs = this.deps.devices.getCameras().find(camera => camera.displayName.includes('OBS'));
      return obs ? { name: 'OBS', deviceId: obs.deviceId } : undefined;
    }
    const cameras = this.deps.devices.getShadowcams();
    // Hub routes (e.g. ShadowCam03) may enumerate under a different pool slot name; resolve via driver state.
    const selected = shadowcamName
      ? resolveShadowcamDevice(shadowcamName, cameras, this.deps.getDriverState?.()) ?? undefined
      : cameras[0];
    return selected ? { name: shadowcamName ?? normalizeShadowcamProfileName(selected.displayName), deviceId: selected.deviceId } : undefined;
  }
  private clearRequest(id: string): void {
    const request = this.requests.get(id);
    if (request) clearTimeout(request.cleanup);
    this.requests.delete(id);
  }
  private now(): number { return this.deps.nowMs?.() ?? Date.now(); }
  private assertCurrent(context: { isCurrent: () => boolean }, deadline: number): void {
    if (!context.isCurrent()) throw new Error('Capture superseded');
    if (this.now() >= deadline) throw new Error('Capture deadline exceeded');
  }
}
